import { FC, useState } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { userRoles, UserZ } from '@libs/schemas';
import { HelpCircle, Plus, Trash2 } from 'lucide-react';
import MultiSelect from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from 'src/trpc';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';

interface UserFormOrganizationAssignmentProps {
  form: UseFormReturn<UserZ>;
}

// Helper function to format organization ID for display
const formatOrganizationId = (id: string): string => {
  return id.length > 8 ? `${id.substring(0, 8)}...` : id;
};

// Filter out 'customer' role from organization role options
const orgRoleOptions = userRoles
  .filter((role) => role !== 'customer')
  .map((role) => ({
    label: role,
    value: role,
  }));

export const UserFormOrganizationAssignment: FC<UserFormOrganizationAssignmentProps> = ({
  form,
}) => {
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  // Fetch the list of organizations
  const { data: organizationsData, isLoading } =
    trpc.admin.organization.getAllOrganizations.useQuery({
      offset: 0,
      limit: 100, // Fetch all organizations for selection
    });

  // Set up field array for organizations
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'organizations',
  });

  // Function to add a new organization
  const handleAddOrganization = () => {
    if (!selectedOrg) return;

    // Check if organization is already added
    const alreadyExists = fields.some((field) => field.organizationId === selectedOrg);
    if (alreadyExists) return;

    append({ organizationId: selectedOrg, roles: [] });
    setSelectedOrg('');
  };

  // Filter out organizations that are already added
  const availableOrganizations = organizationsData?.organizations.filter(
    (org) => !fields.some((field) => field.organizationId === org._id.toString()),
  );

  return (
    <div className="col-span-2 space-y-6 bg-slate-50 p-6 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between">
        <FormLabel className="text-base font-semibold text-text-light">Organizations</FormLabel>
        <Separator className="ml-4 w-32" />
      </div>

      {/* Organization assignment fields */}
      <div className="space-y-4 max-h-[30vh] overflow-y-auto pr-2">
        {fields.map((field, index) => {
          const organization = organizationsData?.organizations.find(
            (org) => org._id.toString() === field.organizationId,
          );

          // Skip orgs the current admin doesn't have access to (not in getAllOrganizations result).
          // Only filter after data has loaded to avoid flickering on initial render.
          if (!isLoading && organizationsData && !organization) return null;

          // Display organization name or fallback
          const orgDisplay =
            organization?.name ||
            `Unnamed Organization (${formatOrganizationId(field.organizationId)})`;

          return (
            <div
              key={field.id}
              className="grid grid-cols-12 gap-4 p-5 border border-slate-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-all"
            >
              <div className="col-span-12 sm:col-span-4 flex items-center space-x-2">
                <span className="font-medium text-slate-700">{orgDisplay}</span>
              </div>

              <div className="col-span-10 sm:col-span-7">
                <Controller
                  control={form.control}
                  name={`organizations.${index}.roles`}
                  render={({ field: roleField }) => (
                    <FormItem>
                      <div className="flex items-center mb-1">
                        <FormLabel className="text-sm font-medium text-text-light">
                          Organization Roles
                        </FormLabel>
                        <Tooltip content="Select the roles this user will have within this organization">
                          <HelpCircle className="h-4 w-4 ml-1 text-slate-400 hover:text-slate-600 cursor-help" />
                        </Tooltip>
                      </div>
                      <FormControl>
                        <MultiSelect
                          options={orgRoleOptions}
                          value={(roleField.value || []).map((role) => ({
                            label: role,
                            value: role,
                          }))}
                          onChange={(selectedOptions) =>
                            roleField.onChange(selectedOptions.map((option) => option.value))
                          }
                          placeholder="Select roles"
                          badgeClassName="px-3 py-1 text-sm bg-primary/10 hover:bg-primary/20 text-primary"
                          inputProps={{
                            className: 'focus:border-primary',
                          }}
                        />
                      </FormControl>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="col-span-2 sm:col-span-1 flex items-center justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  className="text-destructive hover:text-white hover:bg-destructive rounded-full transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add new organization section */}
      <div className="mt-6 p-4 border border-dashed border-slate-300 rounded-xl bg-white/60">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
          <div className="flex-1 w-full">
            <FormLabel className="text-sm font-medium text-text-light mb-1 block">
              Add Organization
            </FormLabel>
            <Select
              value={selectedOrg}
              onValueChange={setSelectedOrg}
              disabled={isLoading || !availableOrganizations?.length}
            >
              <SelectTrigger
                className={cn(
                  'w-full border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all',
                  !availableOrganizations?.length && !isLoading && 'opacity-50',
                )}
              >
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px] overflow-auto rounded-lg shadow-lg">
                {availableOrganizations?.map((org) => (
                  <SelectItem
                    key={org._id.toString()}
                    value={org._id.toString()}
                    className="focus:bg-primary/10 cursor-pointer"
                  >
                    {org.name ||
                      `Unnamed Organization (${formatOrganizationId(org._id.toString())})`}
                  </SelectItem>
                ))}
                {!availableOrganizations?.length && !isLoading && (
                  <SelectItem value="none" disabled className="text-slate-400 italic">
                    No organizations available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="button"
            onClick={handleAddOrganization}
            disabled={!selectedOrg}
            className="bg-primary hover:bg-primary/90 text-white rounded-lg transition-all px-4 h-10 shadow-sm hover:shadow"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Organization
          </Button>
        </div>
      </div>
    </div>
  );
};
